import React, { ReactElement, ReactNode } from "react";
import Head from "next/head";
import { useModal } from "@hooks/useModalContext";
import dynamic from "next/dynamic";
import Mobile from "@components/shared/blogPost/sideWidget/mobile";
import { useDomainContext } from "@hooks/useDomainContext";
import { generateGlobalVariables } from "@page-builder/utils";
import CcpaPrivacyNotice from "@components/shared/legal/ccpaPrivacyNotice";

const Footer = dynamic(() => import("@components/shared/footer"));
const Header = dynamic(() => import("@components/shared/header"));
const Modal = dynamic(() => import("@components/shared/modal"));
const CloseIcon = dynamic(() => import("@components/svg/closeIcon"));
const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);

export default function UnsubscribeWrapper({
    children,
    className = "",
}: {
    children: ReactNode;
    className?: String;
}): ReactElement {
    const { modal, closeModal } = useModal();
    const { domain, routeConfig, locale, domainNameTranslation } =
        useDomainContext();

    const handleClose = () => {
        closeModal();
    };

    const { active: modalActive, content: modalContent } = modal;

    const getModalContent = () => {
        switch (modalContent) {
            case "privacy":
                return <PrivacyPolicy />;
            case "terms":
                return <TermsAndConditions />;
            case "sideWidget":
                return <Mobile />;
            case "ccpaPrivacyNotice":
                return <CcpaPrivacyNotice />;
            default:
                return null;
        }
    };

    const templateGlobalConfig = routeConfig?.templateConfig;

    const getFinalFontFamily = () => {
        if (templateGlobalConfig?.theme?.fontFamily) {
            return templateGlobalConfig?.theme?.fontFamily;
        }
        return locale === "ar" ? "Almarai" : "Poppins";
    };

    const fontFamily = getFinalFontFamily();

    const globalVariables = templateGlobalConfig
        ? generateGlobalVariables(templateGlobalConfig)
        : {};

    const remappedThemeVariables = {
        "--primary-darken-1": "var(--global-theme-shades-darken1)",
        "--primary-darken-2": "var(--global-theme-shades-darken2)",
        "--primary-darken-3": "var(--global-theme-shades-darken3)",
        "--primary-darken-4": "var(--global-theme-shades-darken4)",
        "--primary-darken-5": "var(--global-theme-shades-darken5)",
        "--primary": "var(--global-theme-shades-primary)",
        "--primary-lighten-1": "var(--global-theme-shades-lighten1)",
        "--primary-lighten-2": "var(--global-theme-shades-lighten2)",
        "--primary-lighten-3": "var(--global-theme-shades-lighten3)",
        "--primary-lighten-4": "var(--global-theme-shades-lighten4)",
        "--primary-lighten-5": "var(--global-theme-shades-lighten5)",
    };

    const modalTitles: Record<string, string> = {
        privacy:
            domain?.translations?.[`${domainNameTranslation}_PrivacyPolicy`] ??
            "Privacy Policy",
        terms:
            domain?.translations?.[
                `${domainNameTranslation}_TermsConditions`
            ] ?? "Terms and Conditions",
        sideWidget: "Contents",
    };

    return (
        <>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
                <link
                    href={`https://fonts.googleapis.com/css2?family=${fontFamily}:wght@300;400;500;600;700;900&display=swap`}
                    rel="stylesheet"
                />
            </Head>

            <div
                style={{
                    fontFamily: `'${fontFamily}', sans-serif`,
                    ...globalVariables,
                    ...remappedThemeVariables,
                }}
                className="flex flex-col min-h-screen"
            >
                <Header
                    backGroundColor={domain.config?.headerBackgroundColor}
                    boxShadow="0px -2px 10px #888888"
                    slogan={domain.slogan}
                    textColor={domain.config?.sloganTextColor}
                    useSecondaryLogo={
                        domain?.config?.globalPageUseSecondaryLogo || false
                    }
                />
                <main
                    className={`max-w-7xl mx-auto flex items-center justify-center px-4vw lg:px-4 mt-10 mb-auto w-full ${
                        className as string
                    }`}
                >
                    {children}
                </main>
                <Footer />
                <Modal
                    onClose={handleClose}
                    active={modalActive}
                    closeIcon={<CloseIcon />}
                    backdropClosable
                    modalStyles={{
                        display: "flex",
                        flexDirection: "column",
                        maxHeight:
                            modalContent === "sideWidget" ? "80vh" : "100%",
                    }}
                    modalClassName="h-[90vh] rounded"
                    bodyStyles={{ overflowY: "scroll" }}
                    width={900}
                    header={modalTitles[modalContent ?? ""]}
                    fontFamilyAsProp={fontFamily}
                >
                    {getModalContent()}
                </Modal>
            </div>
        </>
    );
}
